import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, getIn } from 'formik';

import addNumberPronounSuffix from 'common/util/addNumberPronounSuffix';

import {
  SR_API_ACTION_GUARANTOR_INVITE,
} from 'api/actionTypes';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_APPLICATION_STEP_COMPLETED,
} from 'client/analytics/eventList';

import {
  createInitialValues,
  createFormValidationFn,
  getNextPendingRequiredFormField,
} from 'client/helpers/formikHelpers';

import {
  isListingTenantScreening,
} from 'common/listingHelpers';

import {
  SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR,
} from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import ListingActions from 'client/actions/Listing';
import NavigatorActions from 'client/actions/Navigator';
import HouseholdActions from 'client/actions/Household';
import postWithState from 'client/actions/postWithState';
import Navbar from 'client/components/Navbar';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW,
} from 'client/components/ButtonDeprecated';

import branch from 'client/core/branch';

const getFormFields = ({
  listing,
  formValues = {},
}) => {
  const formFields = [
    {
      id: 'guarantorCount',
      title: 'Do you need any Guarantors?',
      label: (
        'They are not your roommates. ' +
        'A guarantor is someone who can financially back you (parent, guardian, relative). ' +
        'They are required if your income is less than 3X the rent.'
      ),
      validations: ['required'],
      type: listing && isListingTenantScreening(listing)
        ? 'hidden'
        : 'multiple_choice',
      properties: {
        multipleSelection: false,
        choices: [
          {
            label: 'None',
            value: 0,
          },
          {
            label: '1',
            value: 1,
          },
          {
            label: '2',
            value: 2,
          },
        ],
      },
    },
  ];
  for (let index = 0; index < (formValues.guarantorCount || 0); index += 1) {
    const guarantorName = ((formValues.guarantors || [])[index] || {}).name || '';
    formFields.push(...[
      {
        id: `guarantors[${index}].name`,
        title: `${addNumberPronounSuffix(index + 1)} Guarantor name?`,
        validations: ['required'],
        properties: {
          placeholder: 'Billy Bob',
        },
        type: 'short_text',
      },
      {
        id: `guarantors[${index}].email`,
        title: `${guarantorName}'s email?`,
        validations: ['required', 'email'],
        properties: {
          placeholder: 'Write here...',
        },
        type: 'email',
      },
      {
        id: `guarantors[${index}].phone`,
        title: `${guarantorName}'s phone?`,
        validations: ['required', 'phone'],
        properties: {
          placeholder: '5125551234',
        },
        type: 'phone',
      },
    ]);
  }
  return formFields;
};

@withTransitionHook
@withScrollToTop
@withApiState({
  createMerchantApiState: {
    action: SR_API_ACTION_GUARANTOR_INVITE,
  },
})
@branch({
  currentUser: ['currentUser'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  roommateApplication: ['roommateApplication'],
  listing: ['listing'],
})
@withScreenId(SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR)
class HouseholdInviteGuarantor extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Invite Guarantors | Sunroom Rentals',
  })

  static fetchData(match, inject) {
    return Promise.all([
      ListingActions.getById(inject, match.params.listingId),
      HouseholdActions.loadRenterProfile(inject, {
        userId: parseInt(match.params.userId, 10),
      }),
    ]);
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    renterProfilesByUserId: PropTypes.object,
  }

  constructor(props) {
    super(props);
    this.state = {
      formFieldId: null,
    };
  }

  componentDidMount() {
    const { currentUser, renterProfilesByUserId } = this.props;
    const renterProfile = renterProfilesByUserId[currentUser.id];

    this.setState({
      formFieldId: getFormFields({
        listing: this.props.listing,
        formValues: {
          guarantorCount: renterProfile.guarantorCount,
        },
      }).find(({ type }) => type !== 'hidden').id,
    });
  }

  _onSubmit = (formValues) => {
    const {
      listing,
      match,
      dispatch,
      history,
    } = this.props;

    const formFields = getFormFields({
      listing,
      formValues,
    });
    const { nextFormField } = getNextPendingRequiredFormField({
      formFields: formFields,
      values: formValues,
    });
    const listingId = parseInt(match.params.listingId, 10);
    const userId = parseInt(match.params.userId, 10);

    if (nextFormField) {
      this.setState({
        formFieldId: nextFormField.id,
      });
    }
    else {
      analytics.track(ANALYTICS_EVENT_APPLICATION_STEP_COMPLETED, {
        applicationStepId: 'inviteGuarantor',
      });

      dispatch(postWithState, {
        action: SR_API_ACTION_GUARANTOR_INVITE,
        payload: {
          guarantors: formValues.guarantors || [],
          listingId: listingId,
          userId: userId,
        },
      })
        .then(() => {
          if (formValues.guarantors && formValues.guarantors.length) {
            history.push(`/application/${userId}/${listingId}/invite-guarantor-sent`);
          }
          else {
            return dispatch(HouseholdActions.roommateApplicationStep, {
              listingId: listingId,
            });
          }
        });
    }
  }

  _onBack = (formValues) => {
    const {
      listing,
      match,
      dispatch,
    } = this.props;

    const {
      formFieldId,
    } = this.state;

    const formFields = getFormFields({
      listing,
      formValues,
    }).filter(({ type }) => type !== 'hidden');

    const { lastFormField } = getNextPendingRequiredFormField({ formFields, values: formValues });

    if (formFieldId === formFields[0].id) {
      const { userId, listingId } = match.params;

      if (isListingTenantScreening(listing)) {
        dispatch(NavigatorActions.replace, {
          to: `/profile?viewingListingId=${listing.id}`,
        });
        return;
      }

      dispatch(NavigatorActions.replace, {
        to: `/application/${userId}/${listingId}/roommate-count`,
      });
    }
    else {
      this.setState({
        formFieldId: lastFormField.id,
      });
    }
  }

  render() {
    const {
      listing,
    } = this.props;
    const {
      formFieldId,
    } = this.state;
    const { currentUser, renterProfilesByUserId } = this.props;
    const renterProfile = renterProfilesByUserId[currentUser.id];

    const initialValues = createInitialValues({
      formFields: getFormFields({
        listing,
        formValues: {
          guarantorCount: renterProfile.guarantorCount,
        },
      }),
      defaultValues: {
        guarantorCount: renterProfile.guarantorCount,
      },
    });
    const initialFormField = getFormFields({
      listing,
      formValues: initialValues,
    }).find(({ id }) => formFieldId === id);

    if (formFieldId === null) {
      return null;
    }

    return (
      <div className='page-inviteGuarantor'>
        <div className='inviteRoommates'>
          <Formik
            initialValues={initialValues}
            isInitialValid={initialFormField && createFormValidationFn([initialFormField])(initialValues)}
            validate={(formValues) => {
              const formField = getFormFields({
                listing,
                formValues,
              }).find(({ id }) => formFieldId === id);
              return createFormValidationFn([formField])(formValues);
            }}
            onSubmit={this._onSubmit}
            render={(formikBag) => {
              const formField = getFormFields({
                listing,
                formValues: formikBag.values,
              })
                .find(({ id }) => formFieldId === id);

              const formValue = getIn(formikBag.values, formFieldId);

              return (
                <div>
                  <Navbar
                    leftButton={(
                      <div
                        className='link back'
                        onClick={() => this._onBack(formikBag.values)}
                      />
                    )}
                  />
                  <div className='stepContainer'>
                    <Form>
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                      <div>
                        <ButtonDeprecated
                          type='submit'
                          theme={BUTTON_THEME_YELLOW}
                          disabled={!formikBag.isValid || (typeof formValue !== 'number' && !formValue)}
                          onClick={formikBag.handleSubmit}
                        >
                          next
                        </ButtonDeprecated>
                      </div>
                    </Form>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    );
  }
}

HouseholdInviteGuarantor.displayName = 'HouseholdInviteGuarantor';

export default HouseholdInviteGuarantor;
